import React, { useState, useEffect } from 'react'
import styled from 'styled-components'
import {
  Modal,
  Form,
  Radio,
  InputNumber,
  Input,
  Typography,
  message
} from 'antd'
import { useMediaQuery } from 'react-responsive'
import { updateInventoryProductStock } from '../../api/inventory'
import Hint from '../common/Hint'

const { Text } = Typography

// 操作按鈕在手機版等寬、好點按
const OperationGroup = styled(Radio.Group)`
  display: flex;
  width: 100%;
  .ant-radio-button-wrapper {
    flex: 1;
    text-align: center;
  }
  @media screen and (max-width: 768px) {
    .ant-radio-button-wrapper {
      height: 44px;
      line-height: 42px;
      font-size: 15px;
    }
  }
`

const StockHeader = styled.div`
  margin-bottom: 16px;
  @media screen and (max-width: 768px) {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    background: #fafafa;
    border-radius: 8px;
    padding: 10px 12px;
    margin-bottom: 12px;
  }
`

// 獨立「調整庫存」流程：走專用 PATCH /products/:id/stock，
// 後端會寫入 StockAdjustment，明細會出現「庫存調整（增/減）」一列。
const StockAdjustModal = ({ open, product, onClose, onSuccess }) => {
  const isMobile = useMediaQuery({ maxWidth: 768 })
  const [form] = Form.useForm()
  const [submitting, setSubmitting] = useState(false)

  const currentStock = Number(product?.stock) || 0
  const unit = product?.unit || '件'

  // 監聽，計算調整後庫存供預覽
  const operation = Form.useWatch('operation', form) || 'add'
  const quantity = Form.useWatch('quantity', form)

  useEffect(() => {
    if (open) {
      form.resetFields()
      form.setFieldsValue({ operation: 'add' })
    }
  }, [open, form])

  const previewStock = (() => {
    const q = Number(quantity)
    if (!q || q <= 0) return null
    if (operation === 'add') return currentStock + q
    if (operation === 'subtract') return currentStock - q
    if (operation === 'set') return q
    return null
  })()

  const previewInvalid = previewStock !== null && previewStock < 0

  const handleOk = async () => {
    try {
      const values = await form.validateFields()
      if (
        values.operation === 'subtract' &&
        Number(values.quantity) > currentStock
      ) {
        message.error('減少數量不可大於現有庫存')
        return
      }
      setSubmitting(true)
      await updateInventoryProductStock(product._id || product.id, {
        operation: values.operation,
        quantity: Number(values.quantity),
        reason: values.reason,
        note: values.note || undefined
      })
      message.success('庫存調整成功')
      if (typeof onSuccess === 'function') onSuccess()
      onClose()
    } catch (error) {
      if (error?.errorFields) return // 表單驗證錯誤，不另外提示
      console.error('庫存調整失敗:', error)
      message.error(
        error.response?.data?.message || '庫存調整失敗，請稍後再試'
      )
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Modal
      title={`調整庫存 — ${product?.name || product?.title || ''}`}
      open={open}
      onCancel={onClose}
      onOk={handleOk}
      okText='確認調整'
      cancelText='取消'
      okButtonProps={{ loading: submitting, disabled: previewInvalid }}
      width={isMobile ? '100%' : 480}
      style={
        isMobile
          ? { top: 0, maxWidth: '100vw', margin: 0, paddingBottom: 0 }
          : undefined
      }
      styles={
        isMobile
          ? {
              body: {
                paddingTop: 12,
                paddingLeft: 12,
                paddingRight: 12,
                paddingBottom: 12,
                maxHeight: 'calc(100vh - 110px)',
                overflowY: 'auto'
              },
              header: { paddingLeft: 12, paddingRight: 12 },
              content: { borderRadius: 0, minHeight: '100vh' },
              footer: { padding: 12 }
            }
          : undefined
      }
      destroyOnClose
    >
      <StockHeader>
        <Text type='secondary'>現有庫存</Text>
        <Text strong style={{ fontSize: 18, marginLeft: 6 }}>
          {currentStock} {unit}
        </Text>
      </StockHeader>

      <Form form={form} layout='vertical' initialValues={{ operation: 'add' }}>
        <Form.Item name='operation' label='操作方式'>
          <OperationGroup buttonStyle='solid'>
            <Radio.Button value='add'>增加</Radio.Button>
            <Radio.Button value='subtract'>減少</Radio.Button>
            <Radio.Button value='set'>設定為</Radio.Button>
          </OperationGroup>
        </Form.Item>

        <Form.Item
          name='quantity'
          label={operation === 'set' ? '設定庫存為' : '數量'}
          rules={[
            { required: true, message: '請輸入數量' },
            {
              validator: (_, value) =>
                value > 0
                  ? Promise.resolve()
                  : Promise.reject(new Error('數量必須大於 0'))
            }
          ]}
        >
          <InputNumber
            min={0}
            precision={2}
            size={isMobile ? 'large' : 'middle'}
            inputMode='decimal'
            style={{ width: '100%' }}
            placeholder='請輸入數量'
            addonAfter={unit}
          />
        </Form.Item>

        {previewStock !== null && (
          <Hint
            tone={previewInvalid ? 'error' : 'info'}
            style={{ marginBottom: 16 }}
            title={
              previewInvalid
                ? '調整後庫存為負數，請重新確認'
                : `調整後庫存將為 ${previewStock} ${unit}`
            }
          />
        )}

        <Form.Item
          name='reason'
          label='調整原因'
          rules={[{ required: true, message: '請填寫調整原因' }]}
          tooltip='此調整會記錄到該商品的「異動明細」，方便日後追溯。'
        >
          <Input
            size={isMobile ? 'large' : 'middle'}
            placeholder='例如：盤點調整、商品報廢、贈品入庫'
          />
        </Form.Item>

        <Form.Item name='note' label='備註（選填）'>
          <Input.TextArea rows={2} placeholder='補充說明（選填）' />
        </Form.Item>
      </Form>
    </Modal>
  )
}

export default StockAdjustModal
